import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../../components/layout';
import {
  AssistantsSidebar,
  AssistantsList,
  type AssistantCategory,
} from '../../components/assistants';
import { useApp } from '../../context';
import { mockCategories } from '../../data/mockData';
import { api, templateApi, AgentTemplate } from '../../services/api';
import type { Assistant } from '../../types';
import { sanitizeBrandText } from '../../utils/helpers';
import { TemplatesList } from '../../components/templates/TemplatesList';

export const AssistantsPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, setAssistants } = useApp();

  // Assistants state (from DB)
  const [assistants, setLocalAssistants] = useState<Assistant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Marketplace templates state
  const [templates, setTemplates] = useState<AgentTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Filter state - default to 'project' to show user's private assistants
  const [selectedCategory, setSelectedCategory] = useState<AssistantCategory>('project');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [createdByMe, setCreatedByMe] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState<string>('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  // Load assistants from API
  const loadAssistants = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.listAssistants();
      if (response.success) {
        // Map API response to Assistant type
        const mappedAssistants: Assistant[] = response.data.map((a: any) => ({
          id: a.id,
          name: a.name,
          description: a.description,
          author: sanitizeBrandText(a.authorName || 'Unknown'),
          authorId: a.authorId,
          categories: a.categories || [],
          llmModel: a.llmModel || 'gpt-4o',
          systemInstructions: a.systemInstructions || '',
          conversationStarters: a.conversationStarters || [],
          isShared: a.visibility === 'public',
          likes: a.likes || 0,
          status: a.status || 'active',
          icon: a.icon,
          createdAt: a.createdAt,
          updatedAt: a.updatedAt,
          datasourceIds: a.datasourceIds || [],
          integrationIds: a.integrationIds || [],
        }));
        setLocalAssistants(mappedAssistants);
        setAssistants(mappedAssistants);
      }
    } catch (err) {
      console.error('Failed to load assistants:', err);
      setError('Failed to load assistants. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [setAssistants]);

  // Load assistants on mount
  useEffect(() => {
    loadAssistants();
  }, [loadAssistants]);

  // Load templates when marketplace is selected
  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const response = await templateApi.getMarketplaceTemplates({
        search: searchQuery || undefined,
        sortBy: 'popular',
      });
      if (response.data) {
        setTemplates(response.data);
      }
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setTemplatesLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (selectedCategory === 'marketplace') {
      loadTemplates();
    }
  }, [selectedCategory, loadTemplates]);

  // Handle template clone
  const handleCloneTemplate = async (template: AgentTemplate) => {
    try {
      const response = await templateApi.cloneTemplate(template.id, {
        customName: template.name,
        customDescription: template.description,
      });
      if (response.data?.assistant) {
        navigate(`/assistants/${response.data.assistant.id}`);
      }
    } catch (err) {
      console.error('Failed to clone template:', err);
    }
  };

  // Filter assistants based on category
  const getFilteredAssistants = (): Assistant[] => {
    let filtered = [...assistants];

    // Filter by category tab
    // 'project' shows private assistants, 'marketplace' shows public shared assistants
    if (selectedCategory === 'marketplace') {
      filtered = filtered.filter((a) => a.isShared);
    }
    // 'project' shows all user's assistants (both private and public they created)

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.name.toLowerCase().includes(query) ||
          a.description.toLowerCase().includes(query)
      );
    }

    // Filter by created by me
    if (createdByMe) {
      filtered = filtered.filter((a) => a.author === 'Umapathi Sakirevupalle');
    }

    // Filter by categories
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((a) =>
        a.categories.some((cat) => selectedCategories.includes(cat))
      );
    }

    return filtered;
  };

  const filteredAssistants = getFilteredAssistants();
  const totalPages = Math.ceil(filteredAssistants.length / itemsPerPage);
  const paginatedAssistants = filteredAssistants.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleAssistantClick = (assistant: Assistant) => {
    navigate(`/assistants/${assistant.id}`);
  };

  const handleCreateAssistant = () => {
    navigate('/assistants/create');
  };

  const handleCreateRemoteAssistant = () => {
    navigate('/assistants/create?type=remote');
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const sidebar = (
    <AssistantsSidebar
      selectedCategory={selectedCategory}
      onCategoryChange={setSelectedCategory}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      selectedProject={selectedProject}
      onProjectChange={setSelectedProject}
      categories={mockCategories}
      selectedCategories={selectedCategories}
      onCategoriesChange={setSelectedCategories}
      createdByMe={createdByMe}
      onCreatedByMeChange={setCreatedByMe}
      selectedCreator={selectedCreator}
      onCreatorChange={setSelectedCreator}
    />
  );

  return (
    <AppLayout sidebar={sidebar}>
      {selectedCategory === 'marketplace' ? (
        <TemplatesList
          templates={templates}
          isLoading={templatesLoading}
          onClone={handleCloneTemplate}
          searchQuery={searchQuery}
        />
      ) : (
        <AssistantsList
          assistants={paginatedAssistants}
          totalCount={filteredAssistants.length}
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onAssistantClick={handleAssistantClick}
          onCreateAssistant={handleCreateAssistant}
          onCreateRemoteAssistant={handleCreateRemoteAssistant}
        />
      )}
    </AppLayout>
  );
};

export default AssistantsPage;
